/**
 * Builds the exact payload for Generate / Apply & Generate action
 * matching the EmployeeReportRequestDto C# specification:
 *
 * {
 *   "CorporationPcMappings": [
 *     { "CorpID": "...", "PCID": "..." },
 *     { "CorpID": "...", "PCID": null }
 *   ],
 *   "DepartmentIdList": "...",
 *   "JobIdList": "...",
 *   "FromDate": "...",
 *   "ToDate": "...",
 *   "ReportedBy": 1 // 1 = Daily, 2 = Weekly, 3 = Monthly
 * }
 */
export const buildGeneratePayload = ({
  filters = {},
  reportType = 'daily',
  selectedFields = [],
  selectedRecords = [],
  fallbackCorpId,
}) => {
  const records =
    selectedRecords && selectedRecords.length > 0
      ? selectedRecords
      : filters.selectedCorporationRecords || [];

  // 1. Paired Corporation & PC Mapping
  let corporationPcMappings = [];

  if (records.length > 0) {
    corporationPcMappings = records.map((r) => {
      const corpId = String(r.CorporationID || r.corpId || '').trim();
      const rawPcid = r.PCID !== null && r.PCID !== undefined ? r.PCID : (r.pcId ?? r.ProfitCenterID);
      let pcid = null;
      if (rawPcid !== null && rawPcid !== undefined) {
        const s = String(rawPcid).trim();
        if (s && s.toLowerCase() !== 'null' && s.toLowerCase() !== 'undefined') {
          pcid = s.startsWith('0x') || s.startsWith('0X') ? s.slice(2) : s;
        }
      }
      return {
        CorpID: corpId,
        PCID: pcid,
      };
    });
  } else if (filters.selectedCorporationIds && filters.selectedCorporationIds.length > 0) {
    corporationPcMappings = filters.selectedCorporationIds.map((id) => {
      const parts = id.split('_');
      const corpId = parts[0] ? parts[0].trim() : '';
      let pcid = null;
      if (parts.length >= 4) {
        const s = parts[parts.length - 1].trim();
        if (s && s.toLowerCase() !== 'null' && s.toLowerCase() !== 'undefined') {
          pcid = s.startsWith('0x') || s.startsWith('0X') ? s.slice(2) : s;
        }
      }
      return {
        CorpID: corpId,
        PCID: pcid,
      };
    });
  } else if (fallbackCorpId && fallbackCorpId.toLowerCase() !== 'all') {
    corporationPcMappings = [
      {
        CorpID: fallbackCorpId,
        PCID: null,
      },
    ];
  }

  // 2. Department & Job ID lists (if ALL or not specific, send all IDs comma-separated)
  let departmentIdList = null;
  if (filters.department && String(filters.department).toLowerCase() !== 'all') {
    departmentIdList = String(filters.department).trim();
  } else if (filters.allDepartmentIds) {
    if (Array.isArray(filters.allDepartmentIds) && filters.allDepartmentIds.length > 0) {
      departmentIdList = filters.allDepartmentIds.join(',');
    } else if (typeof filters.allDepartmentIds === 'string' && filters.allDepartmentIds.trim() !== '') {
      departmentIdList = filters.allDepartmentIds.trim();
    }
  }

  let jobIdList = null;
  if (filters.jobTitle && String(filters.jobTitle).toLowerCase() !== 'all') {
    jobIdList = String(filters.jobTitle).trim();
  } else if (filters.allJobTitleIds) {
    if (Array.isArray(filters.allJobTitleIds) && filters.allJobTitleIds.length > 0) {
      jobIdList = filters.allJobTitleIds.join(',');
    } else if (typeof filters.allJobTitleIds === 'string' && filters.allJobTitleIds.trim() !== '') {
      jobIdList = filters.allJobTitleIds.trim();
    }
  }

  // 3. Dates
  const fromDate = filters.fromDate || '';
  const toDate = filters.toDate || '';

  // 4. ReportedBy: 1 = Daily, 2 = Weekly, 3 = Monthly
  let reportedBy = 1;
  const rt = String(reportType || filters.reportType || '').toLowerCase();
  if (rt === '2' || rt === 'weekly') {
    reportedBy = 2;
  } else if (rt === '3' || rt === 'monthly') {
    reportedBy = 3;
  } else {
    reportedBy = 1;
  }

  // 5. Selected Fields flags
  const fields = selectedFields || [];
  const scheduledHours = true;
  const actualHours = true;
  const status = fields.includes('status');
  const otPercent = fields.includes('otPercent') || fields.includes('otPercentage');
  const otRisk = fields.includes('otRisk');

  return {
    CorporationPcMappings: corporationPcMappings,
    DepartmentIdList: departmentIdList,
    JobIdList: jobIdList,
    FromDate: fromDate,
    ToDate: toDate,
    ReportedBy: reportedBy,
    ScheduledHours: scheduledHours,
    ActualHours: actualHours,
    Status: status,
    OTPercent: otPercent,
    OTRisk: otRisk,
  };
};


